import { useMemo, useState } from 'react';
import {
  formatHours,
  formatMoney,
  periodOn,
  toWireDate,
  type EmploymentType,
  type LeaveQuota,
  type LeaveType,
  type Metafield,
  type OnboardingQuota,
  type OnboardingRate,
  type OnboardingSchedule,
  type Person,
  type TeamRef,
  type RateAssignment,
  type RateCardTemplate,
  type MemberSkill,
  type Skill,
  type SkillLevel,
  type RateSource,
  type RateUnit,
  type WorkScheduleAssignment,
  type WorkScheduleTemplate,
  type WorkScheduleType,
  type WorkspaceCurrency,
} from '@forecastify/shared';
import {
  Button,
  Dialog,
  DropdownMenu,
  Icon,
  MultiSelect,
  Select,
  Switch,
  TextInput,
  Tooltip,
  type IconName,
} from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { Field } from '../card';
import { MetafieldForm } from '../metafields/metafield-form';
import { employmentLabel } from './people-screen';
import { DateField } from '../date-field';
import { leaveIcon } from '../leave/people-look';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The seven-step onboarding wizard — `main.jsx` 4454–4500, markup 3262+.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `OB_STEPS`, in the mockup's order: **Alapadatok · Közvetlen vezető · Jogosultság ·
 * Munkakörnyezet · Időrögzítés · Metafieldek · Összegzés.** A 900px dialog with the step rail on
 * the left.
 *
 * ⚠️⚠️ **NOTHING IS SAVED UNTIL THE LAST STEP, AND THAT IS THE DESIGN RATHER THAN AN OPTIMISATION.**
 * Every field here is one the person detail can already edit; what the wizard adds is a *guided
 * first pass* that either completes or leaves no trace. `POST /people/:id/onboarding` applies all of
 * it in one transaction — so closing the dialog at step four changes nothing, and there is no
 * half-onboarded state for anybody to discover later.
 *
 * ⚠️ **The steps are navigable in any order**, as the mockup's are, and you can jump back. Forcing
 * a linear path would make correcting step two mean redoing steps three to six.
 *
 * ⚠️⚠️ **A STEP TICKS ONLY WHEN SOMETHING IN IT CHANGED — `decisions.md` U-12, OVERRIDING THE
 * MOCKUP.** Its `obGo` ticks a step the moment you move *past* it, so clicking *Tovább* ten times
 * drew ten ticks over a wizard in which nothing had been entered — and the ticks read as *"done"*.
 * A tick now means *"this step holds something other than what it opened with"* (`changedSteps`
 * below), which is also why undoing a change unticks it again.
 *
 * ⚠️ **Nothing here is required**, deliberately. The mockup validates nothing either, and the fields
 * are facts about a colleague that whoever is onboarding them may genuinely not have yet — a wizard
 * that cannot be finished without a phone number is a wizard people abandon and then never run.
 */

export const OB_STEPS = [
  { key: 'basic', icon: 'user-round' },
  { key: 'org', icon: 'users' },
  { key: 'perm', icon: 'shield-check' },
  { key: 'env', icon: 'globe' },
  { key: 'time', icon: 'clock' },
  /**
   * ⚠️⚠️ **TEN STEPS, IN THE MOCKUP'S OWN ORDER.** `sched` and `leave` were a Phase 3 deferral that
   * `PHASE-4.md` recorded and nothing closed; `rate` is Phase 4's own addition, and it sits between
   * them because that is where `OB_STEPS` puts it — a person's schedule decides what their day is
   * worth in hours, their rate what an hour is worth in money, their leave what they may take off.
   */
  { key: 'sched', icon: 'calendar-days' },
  { key: 'rate', icon: 'credit-card' },
  /**
   * C54 · ⚠️ **Between the rate and the leave, which is the mockup's own `navDef` order.** Nine
   * `obSkill*` bindings are drawn in its markup — worth stating because the person screen's
   * `persSkill*` accessors are drawn by NOTHING, and that surface is therefore not built.
   */
  { key: 'skill', icon: 'sparkles' },
  { key: 'leave', icon: 'sun' },
  { key: 'meta', icon: 'tag' },
  { key: 'done', icon: 'circle-check' },
] as const satisfies readonly { key: string; icon: IconName }[];

export type ObStep = (typeof OB_STEPS)[number]['key'];

function stepLabel(t: Translate, key: ObStep): string {
  /* One `t()` per branch with a literal key — a computed key is invisible to anything scanning. */
  if (key === 'basic') return t('onboarding.step.basic');
  if (key === 'org') return t('onboarding.step.org');
  if (key === 'perm') return t('onboarding.step.perm');
  if (key === 'env') return t('onboarding.step.env');
  if (key === 'time') return t('onboarding.step.time');
  if (key === 'sched') return t('onboarding.step.sched');
  if (key === 'rate') return t('onboarding.step.rate');
  if (key === 'skill') return t('onboarding.step.skill');
  if (key === 'leave') return t('onboarding.step.leave');
  if (key === 'meta') return t('onboarding.step.meta');
  return t('onboarding.step.done');
}

export interface OnboardingDraft {
  phone: string;
  employmentType: EmploymentType;
  /** R-60 · every team they are in, by id. */
  teamIds: string[];
  managerMemberId: string;
  /** 17 Oct 2026 · the `org` step's *Alap projekt szerep*. `''` is *not chosen*. */
  defaultProjectRoleId: string;
  location: string;
  timezone: string;
  language: 'hu' | 'en';
  tracksTime: boolean;
  /** ux-pass-2 · the `time` step's second switch — the mockup's `obPlan`. */
  resourcePlanned: boolean;
  startsOn: string;
  roleIds: string[];
  metafields: Record<string, unknown>;

  /**
   * C61 · the `rate` step. ⚠️ **Strings, because they are what a form holds** — `rateCost` is the
   * text somebody is halfway through typing, and `4 ` is not a number until the draft becomes a
   * body. `onboardingRate` below does that conversion, once.
   */
  rateSkip: boolean;
  rateSource: RateSource;
  rateTemplateId: string;
  rateUnit: RateUnit;
  rateCurrency: string;
  rateCost: string;
  rateTarget: string;
  rateFrom: string;

  /** C64 · the `sched` step. ⚠️ Seven strings, because seven number inputs hold text. */
  schedSource: 'template' | 'custom';
  schedTemplateId: string;
  schedType: WorkScheduleType;
  schedDays: string[];
  schedWeekly: string;
  schedFrom: string;

  /**
   * C54 · the `skill` step.
   *
   * ⚠️ **Pairs rather than a map**, because the ORDER somebody added them in is what the list
   * shows back, and an object's key order is not a thing to rely on for something a person sees.
   */
  skills: readonly { skillId: string; levelId: string }[];

  /** C64 · the `leave` step. The switch, and one typed figure per quota-bearing type. */
  leaveManaged: boolean;
  quotas: Record<string, string>;
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ U-12 · which draft fields belong to which step.                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **`done` owns nothing and therefore never ticks.** It is the summary; finishing is the act,
 * and a tick on it would claim something was entered there.
 */
const STEP_FIELDS: Record<ObStep, readonly (keyof OnboardingDraft)[]> = {
  basic: ['phone', 'startsOn', 'employmentType', 'teamIds'],
  org: ['managerMemberId', 'defaultProjectRoleId'],
  perm: ['roleIds'],
  env: ['location', 'timezone', 'language'],
  time: ['tracksTime', 'resourcePlanned'],
  sched: ['schedSource', 'schedTemplateId', 'schedType', 'schedDays', 'schedWeekly', 'schedFrom'],
  rate: [
    'rateSkip',
    'rateSource',
    'rateTemplateId',
    'rateUnit',
    'rateCurrency',
    'rateCost',
    'rateTarget',
    'rateFrom',
  ],
  skill: ['skills'],
  leave: ['leaveManaged', 'quotas'],
  meta: ['metafields'],
  done: [],
};

/**
 * ⚠️⚠️ **AN EMPTY VALUE AND AN ABSENT KEY ARE THE SAME ANSWER.** Typing a quota and deleting it
 * again leaves `{ [id]: '' }` where the step opened with no key at all, and a metafield cleared
 * back to nothing is `null` or `[]` — none of which is a change anybody made. Keys are sorted so
 * the order a map was filled in cannot read as a difference either.
 */
function comparable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(comparable);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(
          ([, one]) =>
            one !== undefined &&
            one !== null &&
            one !== '' &&
            !(Array.isArray(one) && one.length === 0),
        )
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([key, one]) => [key, comparable(one)]),
    );
  }
  return value;
}

/**
 * U-12 · the steps whose own fields differ from the values the wizard opened with.
 *
 * ⚠️ **Against the SNAPSHOT taken when the wizard opened**, never against a freshly derived seed:
 * the rate and schedule seeds are recomputed when their data arrives, and comparing against a
 * moving baseline would tick a step nobody touched.
 */
export function changedSteps(opening: OnboardingDraft, draft: OnboardingDraft): Set<ObStep> {
  const changed = new Set<ObStep>();
  for (const entry of OB_STEPS) {
    const differs = STEP_FIELDS[entry.key].some(
      (field) =>
        JSON.stringify(comparable(opening[field])) !== JSON.stringify(comparable(draft[field])),
    );
    if (differs) changed.add(entry.key);
  }
  return changed;
}

/** ⚠️ Monday first, as `work_days_ok` and every other seven-day shape in this product. */
const EMPTY_WEEK = ['8', '8', '8', '8', '8', '0', '0'];

/**
 * The draft's schedule step as the endpoint's `schedule` — `null` when none should be written.
 *
 * ⚠️⚠️ **SOMEBODY WHO DOES NOT TRACK TIME GETS NO SCHEDULE, AND THAT IS THE MOCKUP'S RULE RATHER
 * THAN A SHORTCUT.** `obFinish` writes one only `if (d.tracks)`: a daily plan exists to be measured
 * against logged hours, and giving one to somebody who logs none puts them back into the capacity
 * reports the previous step just took them out of.
 *
 * ⚠️ **A template arm with no template is a skip**, for the same reason the rate step's is: a
 * workspace that has curated no schedules yet would otherwise fail validation at the last click.
 */
export function onboardingSchedule(draft: OnboardingDraft): OnboardingSchedule | null {
  if (!draft.tracksTime) return null;
  if (draft.schedSource === 'template') {
    if (draft.schedTemplateId === '') return null;
    return { source: 'template', fromDate: draft.schedFrom, templateId: draft.schedTemplateId };
  }

  const days = draft.schedDays.map((one) => numberOf(one));
  if (days.length !== 7 || days.some((one) => !Number.isFinite(one) || one < 0 || one > 24)) {
    return null;
  }
  const weekly = numberOf(draft.schedWeekly);
  return {
    source: 'custom',
    fromDate: draft.schedFrom,
    type: draft.schedType,
    days,
    /* ⚠️⚠️ **A FIX WEEK'S TOTAL IS ITS DAYS, NOT A SECOND FIGURE SOMEBODY TYPED.** The flex arm is
       the only one where the weekly hours are an input — a fixed week that disagreed with its own
       seven days would be two answers to one question, and the kernel reads both. */
    weeklyHours:
      draft.schedType === 'flex'
        ? Number.isFinite(weekly)
          ? weekly
          : 0
        : days.reduce((sum, one) => sum + one, 0),
  };
}

/**
 * The draft's quota rows.
 *
 * ⚠️ **Only what somebody actually typed.** An empty box is *"nobody has decided yet"*; writing it
 * as `0` would hand the request screen an entitlement of nothing to enforce.
 */
export function onboardingQuotas(draft: OnboardingDraft): OnboardingQuota[] {
  if (!draft.leaveManaged) return [];
  return Object.entries(draft.quotas)
    .filter(([, value]) => value.trim() !== '')
    .map(([leaveTypeId, value]) => ({ leaveTypeId, days: Math.round(numberOf(value)) }))
    .filter((one) => Number.isFinite(one.days) && one.days >= 0 && one.days <= 365);
}

/**
 * The draft's rate step as the endpoint's `rate` — `null` when skipped, or when the step could not
 * be filled in at all.
 *
 * ⚠️⚠️ **A TEMPLATE ARM WITH NO TEMPLATE IS A SKIP, NOT AN EMPTY ASSIGNMENT.** A workspace that has
 * curated no rate cards yet leaves the picker empty, and sending `templateId: ''` would fail schema
 * validation at the end of a wizard somebody just spent two minutes on. The mockup's `obFinish`
 * takes the same guard — `if (rRec.src !== 'template' || rRec.tpl)`.
 */
export function onboardingRate(draft: OnboardingDraft): OnboardingRate | null {
  if (draft.rateSkip) return null;
  if (draft.rateSource === 'template') {
    if (draft.rateTemplateId === '') return null;
    return { source: 'template', fromDate: draft.rateFrom, templateId: draft.rateTemplateId };
  }
  const cost = numberOf(draft.rateCost);
  const target = numberOf(draft.rateTarget);
  if (!Number.isFinite(cost) || !Number.isFinite(target) || cost < 0 || target < 0) return null;
  return {
    source: 'custom',
    fromDate: draft.rateFrom,
    unit: draft.rateUnit,
    currencyCode: draft.rateCurrency,
    cost,
    target,
  };
}

/** ⚠️ `12 500` and `12,5` are both typed here — the settings dialog's own parser, word for word. */
const numberOf = (value: string): number => Number(value.replace(/\s/g, '').replace(',', '.'));

/** The mockup's own timezone list. */
const TIMEZONES = [
  'Europe/Budapest',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Bucharest',
  'America/New_York',
];

export function OnboardingWizard({
  person,
  managers,
  teams,
  roles,
  projectRoles,
  metafields,
  skillVocabulary,
  skillLevels,
  skillsNow,
  rateTemplates,
  currencies,
  rateNow,
  scheduleTemplates,
  scheduleNow,
  leaveTypes,
  quotasNow,
  canSettings,
  serverError,
  onClose,
  onFinish,
}: {
  /** `null` closes the dialog. */
  readonly person: Person | null;
  readonly managers: readonly { id: string; name: string }[];
  readonly teams: readonly TeamRef[];
  readonly roles: readonly { id: string; name: string; code: string }[];
  /** 17 Oct 2026 · the project roles the `org` step offers as *Alap projekt szerep*. */
  readonly projectRoles: readonly { id: string; name: string; color: string }[];
  readonly metafields: readonly Metafield[];
  /**
   * C54 · the `skill` step. ⚠️ **The vocabulary and the scale, because the step offers the
   * first and prices each pick against the second** — and `skillsNow` for the same reason
   * `rateNow` exists: the wizard edits rather than blanks.
   */
  readonly skillVocabulary: readonly Skill[];
  readonly skillLevels: readonly SkillLevel[];
  readonly skillsNow: readonly MemberSkill[];
  /** C61 · the cards the `rate` step can pick from, active ones only. */
  readonly rateTemplates: readonly RateCardTemplate[];
  readonly currencies: readonly WorkspaceCurrency[];
  /**
   * C61 · ⚠️ **What this person is already priced at, so re-running the wizard does not re-price
   * them.** The step seeds from it exactly as the mockup's `obStart` seeds from `rateOf(uid)` —
   * including the start date, so finishing writes over the row it read rather than adding a second.
   */
  readonly rateNow: RateAssignment | null;
  /** C64 · the schedules a workspace curates, active ones only. */
  readonly scheduleTemplates: readonly WorkScheduleTemplate[];
  /** C64 · what this person's schedule is today, so re-running does not re-schedule them. */
  readonly scheduleNow: WorkScheduleAssignment | null;
  /** C64 · the leave types that draw on a quota — an unlimited one has nothing to set. */
  readonly leaveTypes: readonly LeaveType[];
  readonly quotasNow: readonly LeaveQuota[];
  /**
   * ⚠️⚠️ **`settings.manage`, not `people.onboard`** — pricing somebody, scheduling their week and
   * setting their entitlement are all workspace settings, and the server says so on each. False
   * draws each of those three steps' locked pane.
   */
  readonly canSettings: boolean;
  readonly serverError: string | null;
  readonly onClose: () => void;
  readonly onFinish: (draft: OnboardingDraft) => void;
}) {
  const t = useT();
  const [step, setStep] = useState<ObStep>('basic');

  /** ⚠️ The workspace's own default, never a hardcoded `HUF` — C51 lets a workspace pick. */
  const defaultCurrency =
    currencies.find((one) => one.isDefault)?.code ?? currencies[0]?.code ?? 'HUF';

  /**
   * ⚠️ **Seeded from the person, so the wizard EDITS rather than blanks.** Running it on somebody
   * who already has a team must not clear the team by starting empty — the mockup's `obStart` does
   * the same, and the alternative silently undoes work.
   */
  const initial = useMemo<OnboardingDraft | null>(() => {
    if (person === null) return null;
    return {
      phone: person.phone ?? '',
      employmentType: person.employmentType,
      teamIds: person.teams.map((team) => team.id),
      managerMemberId: person.managerMemberId ?? '',
      defaultProjectRoleId: person.defaultProjectRole?.id ?? '',
      location: person.location ?? '',
      timezone: person.timezone,
      language: person.language,
      tracksTime: person.tracksTime,
      resourcePlanned: person.resourcePlanned,
      startsOn: person.startsOn ?? '',
      roleIds: person.roles.slice(0, 1).map((role) => role.id),
      metafields: { ...person.metafields },

      /* ⚠️ Seeded, for `obStart`'s reason: re-running the wizard on somebody who already has
         skills must not silently clear them. */
      skills: skillsNow.map((row) => ({ skillId: row.skillId, levelId: row.levelId })),

      /* C61 · the mockup's own defaults: not skipped, from a template, from today. */
      rateSkip: false,
      rateSource: rateNow?.source ?? 'template',
      rateTemplateId: rateNow?.templateId ?? rateTemplates[0]?.id ?? '',
      rateUnit: rateNow?.unit ?? 'hour',
      rateCurrency: rateNow?.currencyCode ?? defaultCurrency,
      /* ⚠️ Only a CUSTOM row's figures are somebody's typing; a template row's are the template's,
         and pre-filling the custom boxes with them would turn a card into a copy of itself. */
      rateCost: rateNow?.source === 'custom' ? (rateNow.cost ?? '') : '',
      rateTarget: rateNow?.source === 'custom' ? (rateNow.target ?? '') : '',
      rateFrom: rateNow?.fromDate ?? toWireDate(new Date()),

      /* C64 · seeded from what they already have, exactly as the rate step is. */
      schedSource:
        scheduleNow === null ? 'template' : scheduleNow.templateId === null ? 'custom' : 'template',
      schedTemplateId: scheduleNow?.templateId ?? scheduleTemplates[0]?.id ?? '',
      schedType: scheduleNow?.type ?? 'fix',
      /* ⚠️ A template row's days are the TEMPLATE's; seeding the custom boxes from them would make
         a copy of a card the moment somebody looked at the other arm. */
      schedDays:
        scheduleNow !== null && scheduleNow.templateId === null
          ? scheduleNow.days.map((one) => String(Number(one)))
          : [...EMPTY_WEEK],
      schedWeekly:
        scheduleNow !== null && scheduleNow.templateId === null
          ? String(Number(scheduleNow.weeklyHours))
          : '40',
      schedFrom: scheduleNow?.fromDate ?? toWireDate(new Date()),

      leaveManaged: person.leaveManaged,
      quotas: Object.fromEntries(quotasNow.map((one) => [one.leaveTypeId, String(one.days)])),
    };
  }, [
    person,
    rateNow,
    rateTemplates,
    defaultCurrency,
    scheduleNow,
    scheduleTemplates,
    quotasNow,
    skillsNow,
  ]);

  const [draft, setDraft] = useState<OnboardingDraft | null>(initial);
  /* U-12 · ⚠️ What the wizard OPENED with, frozen — `changedSteps` says why it is not `initial`. */
  const [opening, setOpening] = useState<OnboardingDraft | null>(initial);

  /* ⚠️ Re-seeded whenever a different person arrives, or the dialog would show the last one's
   * values. The same reason the calendar's entry dialog re-seeds. */
  const [seededFor, setSeededFor] = useState<string | null>(person?.id ?? null);
  if (person !== null && seededFor !== person.id) {
    setSeededFor(person.id);
    setDraft(initial);
    setOpening(initial);
    setStep('basic');
  }

  if (person === null || draft === null || opening === null) return null;

  const set = (patch: Partial<OnboardingDraft>): void => {
    setDraft({ ...draft, ...patch });
  };

  /**
   * C54 · what the chip row still has to offer, and the level a chip lands at.
   *
   * ⚠️ **Below the `draft === null` guard**, because both read the draft — above it they would
   * have to defend against a draft that does not exist yet, which is what the guard is for.
   *
   * ⚠️ The MIDDLE level, as `skPickOpen` does — never the weakest. Somebody onboarding a
   * colleague is recording what they can do, not what they cannot.
   */
  const alreadyPicked = new Set(draft.skills.map((row) => row.skillId));
  const offerable = skillVocabulary.filter((skill) => !alreadyPicked.has(skill.id));
  const defaultLevel = skillLevels[Math.floor(skillLevels.length / 2)] ?? skillLevels[0];

  const go = (next: ObStep): void => {
    setStep(next);
  };

  /* U-12 · the ticks. Live, so changing a value back unticks its step on the spot. */
  const ticked = changedSteps(opening, draft);

  const index = OB_STEPS.findIndex((entry) => entry.key === step);
  const isLast = step === 'done';

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('onboarding.title', { name: person.displayName })}
      description={t('onboarding.subtitle')}
      closeLabel={t('common.close')}
      width={900}
    >
      <div className="grid gap-4 md:grid-cols-[230px_minmax(0,1fr)]">
        {/* ── the step rail ─────────────────────────────────────────────────────────────── */}
        <nav aria-label={t('onboarding.steps')} className="flex flex-col gap-1">
          <div className="text-muted-foreground mb-1 flex items-center gap-2 px-1 text-[11px] font-medium tracking-wide uppercase">
            <span className="flex-1">{t('onboarding.steps')}</span>
            <span className="tabular-nums">
              {`${String(index + 1)}/${String(OB_STEPS.length)}`}
            </span>
          </div>

          {OB_STEPS.map((entry, position) => (
            <button
              key={entry.key}
              type="button"
              onClick={() => {
                go(entry.key);
              }}
              /* ⚠️ `aria-current`, because "which step am I on" is a state and a border is not
                 audible. */
              aria-current={entry.key === step ? 'step' : undefined}
              className={[
                'flex items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition-colors',
                entry.key === step ? 'border-primary bg-primary/5' : 'hover:bg-accent/50',
              ].join(' ')}
            >
              <span className="bg-muted grid size-5 shrink-0 place-items-center rounded-full text-[10px] tabular-nums">
                {position + 1}
              </span>
              <span className="min-w-0 flex-1 truncate text-[12.5px]">
                {stepLabel(t, entry.key)}
              </span>
              {ticked.has(entry.key) && (
                <Icon name="check" size={13} className="text-primary shrink-0" />
              )}
            </button>
          ))}
        </nav>

        {/* ── the step body ─────────────────────────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-col gap-3">
          <div className="bg-muted/40 flex items-center gap-2 rounded-lg border px-3 py-2">
            <span className="text-[12.5px] font-medium">{person.displayName}</span>
            <span className="text-muted-foreground text-[11.5px]">{person.email}</span>
          </div>

          {step === 'basic' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('people.field.phone')}>
                <TextInput
                  value={draft.phone}
                  onChange={(event) => {
                    set({ phone: event.target.value });
                  }}
                  placeholder="+36 30 123 4567"
                />
              </Field>
              <Field label={t('people.field.startsOn')}>
                <DateField
                  label={t('people.field.startsOn')}
                  value={draft.startsOn}
                  onChange={(startsOn) => {
                    set({ startsOn });
                  }}
                />
              </Field>
              <Field label={t('people.field.employment')}>
                <Select
                  value={draft.employmentType}
                  onChange={(event) => {
                    set({ employmentType: event.target.value as EmploymentType });
                  }}
                  options={[
                    { value: 'internal', label: employmentLabel(t, 'internal') },
                    { value: 'contractor', label: employmentLabel(t, 'contractor') },
                  ]}
                />
              </Field>
              <Field label={t('people.field.teams')}>
                {/* R-60 · any number of teams, from the list Settings → Teams keeps. */}
                <MultiSelect
                  /* U-14 · a list of records, so it searches. */
                  searchable
                  searchPlaceholder={t('list.searchOptions')}
                  label={t('people.field.teams')}
                  placeholder={t('people.field.noTeam')}
                  values={draft.teamIds}
                  onValuesChange={(teamIds) => {
                    set({ teamIds });
                  }}
                  options={teams.map((team) => ({ value: team.id, label: team.name }))}
                />
              </Field>
            </div>
          )}

          {step === 'org' && (
            <div className="flex flex-col gap-3">
              <Field label={t('people.field.manager')}>
                <Select
                  /* U-14 · a list of records, so it searches. */
                  searchable
                  searchPlaceholder={t('list.searchOptions')}
                  value={draft.managerMemberId}
                  onChange={(event) => {
                    set({ managerMemberId: event.target.value });
                  }}
                  options={[
                    { value: '', label: t('people.field.noManager') },
                    ...managers.map((manager) => ({ value: manager.id, label: manager.name })),
                  ]}
                />
              </Field>
              {/* 17 Oct 2026 · the mockup's `obDroleItems` — what each new project starts them as. */}
              <Field label={t('people.field.defaultProjectRole')}>
                <Select
                  value={draft.defaultProjectRoleId}
                  onChange={(event) => {
                    set({ defaultProjectRoleId: event.target.value });
                  }}
                  options={[
                    { value: '', label: t('people.field.noDefaultRole') },
                    ...projectRoles.map((role) => ({ value: role.id, label: role.name })),
                  ]}
                />
              </Field>
            </div>
          )}

          {step === 'perm' && (
            <div className="flex flex-col gap-2">
              <p className="text-muted-foreground text-[12px]">{t('onboarding.permHint')}</p>
              {/**
               * ⚠️⚠️ **ONE ROLE — 13 Oct 2026, the product owner (`decisions.md` R-56).** These were
               * checkboxes, arguing that roles merge; the ruling is that a person has exactly one,
               * which is the mockup's own single `plevel`. A radio group, so the screen cannot
               * express a second, and the server refuses one too.
               */}
              {roles.map((role) => (
                <label
                  key={role.id}
                  className="hover:bg-accent/40 flex items-center gap-2 rounded-md border px-2.5 py-2"
                >
                  <input
                    type="radio"
                    name="onboarding-role"
                    checked={draft.roleIds[0] === role.id}
                    onChange={() => {
                      set({ roleIds: [role.id] });
                    }}
                  />
                  <span className="min-w-0 flex-1 text-[12.5px] font-medium">{role.name}</span>
                  <span className="text-muted-foreground shrink-0 text-[11px]">{role.code}</span>
                </label>
              ))}

              {draft.roleIds.length === 0 && (
                /* ⚠️ Allowed by the API and worth warning about: somebody with no role can do
                 * nothing. Said here rather than refused, because "decide later" is legitimate. */
                <p className="text-muted-foreground text-[11.5px]">
                  {t('onboarding.noRoleWarning')}
                </p>
              )}
            </div>
          )}

          {step === 'env' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('people.field.location')}>
                <TextInput
                  value={draft.location}
                  onChange={(event) => {
                    set({ location: event.target.value });
                  }}
                  placeholder="Budapest"
                />
              </Field>
              <Field label={t('people.field.timezone')}>
                <Select
                  value={draft.timezone}
                  onChange={(event) => {
                    set({ timezone: event.target.value });
                  }}
                  options={[
                    ...TIMEZONES.map((zone) => ({ value: zone, label: zone })),
                    /* ⚠️ Their current zone stays selectable even if it is not in the list — a
                     * picker that cannot represent the stored value would silently change it. */
                    ...(TIMEZONES.includes(draft.timezone)
                      ? []
                      : [{ value: draft.timezone, label: draft.timezone }]),
                  ]}
                />
              </Field>
              <Field label={t('people.field.language')}>
                <Select
                  value={draft.language}
                  onChange={(event) => {
                    set({ language: event.target.value === 'en' ? 'en' : 'hu' });
                  }}
                  options={[
                    { value: 'hu', label: t('people.language.hu') },
                    { value: 'en', label: t('people.language.en') },
                  ]}
                />
              </Field>
            </div>
          )}

          {step === 'time' && (
            <div className="flex flex-col gap-3">
              <Field label={t('people.field.tracks')} note={t('onboarding.timeHint')}>
                <Select
                  value={draft.tracksTime ? 'true' : 'false'}
                  onChange={(event) => {
                    const tracksTime = event.target.value === 'true';
                    /* U-20 · not logging takes planning with it, as the server does. Back on,
                       planning stays where it was left: off, until somebody turns it on. */
                    set(tracksTime ? { tracksTime } : { tracksTime, resourcePlanned: false });
                  }}
                  options={[
                    { value: 'true', label: t('people.tracks.yes') },
                    { value: 'false', label: t('people.tracks.no') },
                  ]}
                />
              </Field>
              {/* ux-pass-2 · the mockup's second switch on this step, *"Erőforrását tervezzük"*,
                  with its two-variant hint — the leave step's own shape. */}
              <label className="flex items-center gap-2.5 rounded-lg border p-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] font-medium">
                    {t('onboarding.plan.label')}
                  </span>
                  <span className="text-muted-foreground block text-[11.5px] leading-relaxed">
                    {!draft.tracksTime
                      ? t('people.plan.needsTracking')
                      : draft.resourcePlanned
                        ? t('onboarding.plan.hint.on')
                        : t('onboarding.plan.hint.off')}
                  </span>
                </span>
                <Switch
                  label={t('onboarding.plan.label')}
                  checked={draft.resourcePlanned}
                  disabled={!draft.tracksTime}
                  onCheckedChange={(next) => {
                    set({ resourcePlanned: next });
                  }}
                />
              </label>
            </div>
          )}

          {step === 'sched' &&
            (!canSettings ? (
              <LockedPane
                title={t('onboarding.sched.locked')}
                note={t('onboarding.sched.lockedHint')}
              />
            ) : !draft.tracksTime ? (
              /**
               * ⚠️⚠️ **SOMEBODY WHO DOES NOT TRACK TIME GETS NO SCHEDULE, AND THE STEP SAYS SO
               * RATHER THAN OFFERING ONE.** A daily plan exists to be measured against logged
               * hours. The mockup's `obFinish` writes one only `if (d.tracks)`, and a form that
               * collected a week nobody would store is a form that lies about what it does.
               */
              <p className="text-muted-foreground rounded-lg border p-3 text-[12.5px]">
                {t('onboarding.sched.notNeeded')}
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={t('onboarding.sched.source')}>
                    <Select
                      value={draft.schedSource}
                      onChange={(event) => {
                        set({
                          schedSource: event.target.value === 'custom' ? 'custom' : 'template',
                        });
                      }}
                      options={[
                        { value: 'template', label: t('onboarding.sched.fromTemplate') },
                        { value: 'custom', label: t('onboarding.sched.fromCustom') },
                      ]}
                    />
                  </Field>
                  <Field label={t('onboarding.sched.from')}>
                    <DateField
                      label={t('onboarding.sched.from')}
                      value={draft.schedFrom}
                      clearable={false}
                      onChange={(schedFrom) => {
                        set({ schedFrom });
                      }}
                    />
                  </Field>
                </div>

                {draft.schedSource === 'template' ? (
                  <Field
                    label={t('onboarding.sched.template')}
                    note={scheduleNote(t, scheduleTemplates, draft.schedTemplateId)}
                  >
                    <Select
                      value={draft.schedTemplateId}
                      onChange={(event) => {
                        set({ schedTemplateId: event.target.value });
                      }}
                      options={[
                        { value: '', label: t('onboarding.sched.pickTemplate') },
                        ...scheduleTemplates.map((one) => ({ value: one.id, label: one.name })),
                      ]}
                    />
                  </Field>
                ) : (
                  <div className="flex flex-col gap-2.5">
                    <Field label={t('onboarding.sched.type')}>
                      <Select
                        value={draft.schedType}
                        onChange={(event) => {
                          set({ schedType: event.target.value === 'flex' ? 'flex' : 'fix' });
                        }}
                        options={[
                          { value: 'fix', label: t('onboarding.sched.fix') },
                          { value: 'flex', label: t('onboarding.sched.flex') },
                        ]}
                      />
                    </Field>

                    {draft.schedType === 'flex' ? (
                      <Field label={t('onboarding.sched.weekly')}>
                        <TextInput
                          value={draft.schedWeekly}
                          className="w-[110px] tabular-nums"
                          onChange={(event) => {
                            set({ schedWeekly: event.target.value });
                          }}
                        />
                      </Field>
                    ) : (
                      /* ⚠️ Seven boxes, Monday first — the mockup's own grid, and the same order
                         the kernel, the templates screen and `work_days_ok` all use. */
                      <div className="flex flex-col gap-1.5">
                        <span className="text-[12px] font-medium">
                          {t('onboarding.sched.days')}
                        </span>
                        <div className="grid grid-cols-7 gap-1.5">
                          {draft.schedDays.map((value, index) => (
                            <label
                              key={WEEKDAY_KEYS[index]}
                              className="flex flex-col items-center gap-1"
                            >
                              <span className="text-muted-foreground text-[10.5px]">
                                {weekdayLabel(t, index)}
                              </span>
                              <TextInput
                                value={value}
                                aria-label={weekdayLabel(t, index)}
                                className="h-8 w-full px-1 text-center tabular-nums"
                                onChange={(event) => {
                                  const next = [...draft.schedDays];
                                  next[index] = event.target.value;
                                  set({ schedDays: next });
                                }}
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    <span className="text-muted-foreground text-[11.5px] tabular-nums">
                      {scheduleTotal(t, draft)}
                    </span>
                  </div>
                )}
              </div>
            ))}

          {step === 'rate' &&
            (!canSettings ? (
              <LockedPane
                title={t('onboarding.rate.locked')}
                note={t('onboarding.rate.lockedHint')}
              />
            ) : (
              <div className="flex flex-col gap-3">
                {/* ⚠️ The skip switch, with the mockup's two-variant hint under the label — it says
                    what skipping COSTS and where to make it good, rather than only that it is
                    possible. */}
                <label className="flex items-center gap-2.5 rounded-lg border p-3">
                  <span className="min-w-0 flex-1">
                    <span className="block text-[12.5px] font-medium">
                      {t('onboarding.rate.skip')}
                    </span>
                    <span className="text-muted-foreground block text-[11.5px] leading-relaxed">
                      {draft.rateSkip
                        ? t('onboarding.rate.skipHint.on')
                        : t('onboarding.rate.skipHint.off')}
                    </span>
                  </span>
                  <Switch
                    label={t('onboarding.rate.skip')}
                    checked={draft.rateSkip}
                    onCheckedChange={(next) => {
                      set({ rateSkip: next });
                    }}
                  />
                </label>

                {!draft.rateSkip && (
                  <>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label={t('onboarding.rate.source')}>
                        <Select
                          value={draft.rateSource}
                          onChange={(event) => {
                            set({
                              rateSource: event.target.value === 'custom' ? 'custom' : 'template',
                            });
                          }}
                          options={[
                            { value: 'template', label: t('onboarding.rate.fromTemplate') },
                            { value: 'custom', label: t('onboarding.rate.fromCustom') },
                          ]}
                        />
                      </Field>
                      <Field label={t('onboarding.rate.from')}>
                        <DateField
                          label={t('onboarding.rate.from')}
                          value={draft.rateFrom}
                          clearable={false}
                          onChange={(rateFrom) => {
                            set({ rateFrom });
                          }}
                        />
                      </Field>
                    </div>

                    {draft.rateSource === 'template' ? (
                      <div className="flex flex-col gap-1.5">
                        <span className="text-[12px] font-medium">
                          {t('onboarding.rate.template')}
                        </span>
                        {/**
                         * ⚠️ **Searchable, and each card carries its rate as a hint** — the
                         * mockup's `obRateTplItems` hint is `cost / target`, which is what makes
                         * this a choice rather than a list of names.
                         */}
                        <DropdownMenu
                          label={t('onboarding.rate.template')}
                          align="start"
                          width={320}
                          searchPlaceholder={t('onboarding.rate.templateSearch')}
                          items={rateTemplates.map((one) => ({
                            label: one.name,
                            hint: rateHint(t, one, currencies),
                            checked: draft.rateTemplateId === one.id,
                            onSelect: () => {
                              set({ rateTemplateId: one.id });
                            },
                          }))}
                        >
                          <button
                            type="button"
                            className="border-input hover:bg-accent/50 flex h-8 w-full items-center gap-2 rounded-md border px-2.5 text-left text-[12.5px] shadow-xs transition-colors"
                          >
                            <span className="min-w-0 flex-1 truncate">
                              {rateTemplates.find((one) => one.id === draft.rateTemplateId)?.name ??
                                t('onboarding.rate.pickTemplate')}
                            </span>
                            <Icon name="chevron-down" size={13} className="opacity-60" />
                          </button>
                        </DropdownMenu>
                        <span className="text-muted-foreground text-[11px] leading-relaxed">
                          {templateNote(t, rateTemplates, currencies, draft.rateTemplateId)}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2.5">
                        <div className="flex flex-col gap-1.5">
                          <span className="text-[12px] font-medium">
                            {t('onboarding.rate.unit')}
                          </span>
                          {/* ⚠️ Two arms, so radios rather than a picker: the mockup draws both
                              stacked because the choice changes what the figures MEAN. */}
                          <div
                            role="radiogroup"
                            aria-label={t('onboarding.rate.unit')}
                            className="flex flex-col gap-1.5"
                          >
                            {(['hour', 'day'] as const).map((unit) => (
                              <button
                                key={unit}
                                type="button"
                                role="radio"
                                aria-checked={draft.rateUnit === unit}
                                onClick={() => {
                                  set({ rateUnit: unit });
                                }}
                                className={[
                                  'flex items-center gap-2.5 rounded-lg border p-2.5 text-left transition-colors',
                                  draft.rateUnit === unit
                                    ? 'border-primary bg-primary/5'
                                    : 'hover:bg-accent/50',
                                ].join(' ')}
                              >
                                <span className="border-primary grid size-4 shrink-0 place-items-center rounded-full border-2">
                                  {draft.rateUnit === unit && (
                                    <span className="bg-primary size-2 rounded-full" />
                                  )}
                                </span>
                                <span className="text-[12.5px] font-medium">
                                  {unit === 'hour'
                                    ? t('settings.rates.unit.hour')
                                    : t('settings.rates.unit.day')}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-3">
                          <Field label={t('onboarding.rate.currency')}>
                            <Select
                              value={draft.rateCurrency}
                              onChange={(event) => {
                                set({ rateCurrency: event.target.value });
                              }}
                              options={currencies.map((one) => ({
                                value: one.code,
                                label: `${one.code} · ${one.name}`,
                              }))}
                            />
                          </Field>
                          <Field label={t('onboarding.rate.cost')}>
                            <TextInput
                              value={draft.rateCost}
                              className="tabular-nums"
                              placeholder="0"
                              onChange={(event) => {
                                set({ rateCost: event.target.value });
                              }}
                            />
                          </Field>
                          <Field label={t('onboarding.rate.target')}>
                            <TextInput
                              value={draft.rateTarget}
                              className="tabular-nums"
                              placeholder="0"
                              onChange={(event) => {
                                set({ rateTarget: event.target.value });
                              }}
                            />
                          </Field>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}

          {step === 'leave' && (
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-2.5 rounded-lg border p-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] font-medium">
                    {t('onboarding.leave.managed')}
                  </span>
                  <span className="text-muted-foreground block text-[11.5px] leading-relaxed">
                    {draft.leaveManaged
                      ? t('onboarding.leave.managedHint.on')
                      : t('onboarding.leave.managedHint.off')}
                  </span>
                </span>
                <Switch
                  label={t('onboarding.leave.managed')}
                  checked={draft.leaveManaged}
                  onCheckedChange={(next) => {
                    set({ leaveManaged: next });
                  }}
                />
              </label>

              {draft.leaveManaged &&
                (!canSettings ? (
                  <LockedPane
                    title={t('onboarding.leave.locked')}
                    note={t('onboarding.leave.lockedHint')}
                  />
                ) : leaveTypes.length === 0 ? (
                  <p className="text-muted-foreground text-[12.5px]">
                    {t('onboarding.leave.noTypes')}
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
                      {t('onboarding.leave.quotas', { year: new Date().getFullYear() })}
                    </span>
                    {/* ⚠️ One row per quota-bearing type. An unlimited type has no entitlement to
                        set, so it is not offered — the mockup filters the same way. */}
                    {leaveTypes.map((type) => (
                      <label
                        key={type.id}
                        className="flex items-center gap-2.5 rounded-lg border px-3 py-2"
                      >
                        {/* ⚠️ The type's own icon, through `leaveIcon` — an unknown lucide name
                            falls back rather than failing, which is the settings screen's rule. */}
                        <Icon
                          name={leaveIcon(type.icon)}
                          size={14}
                          className="text-muted-foreground shrink-0"
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1 truncate text-[12.5px]">{type.name}</span>
                        <TextInput
                          value={draft.quotas[type.id] ?? ''}
                          aria-label={type.name}
                          placeholder="–"
                          className="h-8 w-[80px] tabular-nums"
                          onChange={(event) => {
                            set({ quotas: { ...draft.quotas, [type.id]: event.target.value } });
                          }}
                        />
                        <span className="text-muted-foreground text-[11.5px]">
                          {t('onboarding.leave.days')}
                        </span>
                      </label>
                    ))}
                  </div>
                ))}
            </div>
          )}

          {step === 'skill' && (
            <div className="flex flex-col gap-3">
              {skillVocabulary.length === 0 ? (
                <p className="text-muted-foreground text-[12.5px]">
                  {t('onboarding.skill.noVocabulary')}
                </p>
              ) : (
                <>
                  {/* ⚠️ Chips rather than a select: the mockup's own shape, and the right one
                      — somebody onboarding a colleague adds four or five in a row, and a select
                      that closes after each pick makes that five round trips. */}
                  <div className="flex max-h-[150px] flex-wrap content-start gap-1.5 overflow-y-auto rounded-lg border p-2">
                    {offerable.map((skill) => (
                      <Tooltip key={skill.id} asChild content={skill.categoryName}>
                        <button
                          type="button"
                          className="border-input hover:bg-accent/60 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] transition-colors"
                          onClick={() => {
                            if (defaultLevel === undefined) return;
                            set({
                              skills: [
                                ...draft.skills,
                                { skillId: skill.id, levelId: defaultLevel.id },
                              ],
                            });
                          }}
                        >
                          <span
                            aria-hidden
                            className="size-1.5 shrink-0 rounded-full"
                            style={{ background: skill.categoryColor }}
                          />
                          {skill.name}
                          <Icon name="plus" size={12} className="opacity-60" />
                        </button>
                      </Tooltip>
                    ))}
                    {offerable.length === 0 && (
                      <p className="text-muted-foreground w-full px-2 py-6 text-center text-[11.5px]">
                        {t('onboarding.skill.noneLeft')}
                      </p>
                    )}
                  </div>

                  <div className="overflow-hidden rounded-lg border">
                    <p className="text-muted-foreground bg-muted/40 border-b px-3 py-1.5 text-[10.5px] font-medium tracking-wide uppercase">
                      {t('onboarding.skill.picked')}
                    </p>
                    {draft.skills.map((picked) => {
                      const skill = skillVocabulary.find((one) => one.id === picked.skillId);
                      const level = skillLevels.find((one) => one.id === picked.levelId);
                      if (skill === undefined) return null;
                      return (
                        <div
                          key={picked.skillId}
                          className="hover:bg-accent/40 flex items-center gap-2.5 border-b px-3 py-2 transition-colors last:border-0"
                        >
                          <span
                            aria-hidden
                            className="size-2 shrink-0 rounded-full"
                            style={{ background: skill.categoryColor }}
                          />
                          <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium">
                            {skill.name}
                          </span>
                          <span className="text-muted-foreground w-[120px] shrink-0 truncate text-[11px]">
                            {skill.categoryName}
                          </span>
                          <DropdownMenu
                            items={skillLevels.map((one) => ({
                              label: one.name,
                              /* ⚠️ A second line, not the hint — `skills-section.tsx` says why. */
                              ...(one.description === null || one.description === ''
                                ? {}
                                : { description: one.description }),
                              dot: one.color,
                              checked: one.id === picked.levelId,
                              onSelect: () => {
                                set({
                                  skills: draft.skills.map((row) =>
                                    row.skillId === picked.skillId
                                      ? { ...row, levelId: one.id }
                                      : row,
                                  ),
                                });
                              },
                            }))}
                            align="end"
                            width={300}
                            label={t('onboarding.skill.levelOf', { name: skill.name })}
                          >
                            <button
                              type="button"
                              className="border-input hover:bg-accent/60 flex h-7 w-[140px] shrink-0 items-center gap-2 rounded-md border px-2 text-left text-[12px] shadow-xs transition-colors"
                            >
                              <span
                                aria-hidden
                                className="size-1.5 shrink-0 rounded-full"
                                style={{ background: level?.color ?? 'var(--muted-foreground)' }}
                              />
                              <span className="min-w-0 flex-1 truncate">
                                {level?.name ?? '\u2013'}
                              </span>
                              <Icon
                                name="chevron-down"
                                size={12}
                                className="text-muted-foreground opacity-70"
                              />
                            </button>
                          </DropdownMenu>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t('onboarding.skill.remove', { name: skill.name })}
                            className="text-muted-foreground hover:text-destructive size-7 shrink-0"
                            onClick={() => {
                              set({
                                skills: draft.skills.filter(
                                  (row) => row.skillId !== picked.skillId,
                                ),
                              });
                            }}
                          >
                            <Icon name="x" size={13} />
                          </Button>
                        </div>
                      );
                    })}
                    {draft.skills.length === 0 && (
                      <p className="text-muted-foreground px-3 py-6 text-center text-[11.5px]">
                        {t('onboarding.skill.empty')}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {step === 'meta' &&
            (metafields.length === 0 ? (
              <p className="text-muted-foreground text-[12.5px]">{t('onboarding.noMetafields')}</p>
            ) : (
              <MetafieldForm
                fields={metafields}
                values={draft.metafields}
                errors={{}}
                members={managers.map((manager) => ({ id: manager.id, name: manager.name }))}
                onChange={(key, value) => {
                  set({ metafields: { ...draft.metafields, [key]: value } });
                }}
              />
            ))}

          {step === 'done' && (
            <dl className="flex flex-col gap-1.5 text-[12.5px]">
              {/* ⚠️ A summary of what WILL be written, because nothing has been yet. */}
              <Summary label={t('people.field.phone')} value={draft.phone} />
              <Summary label={t('people.field.startsOn')} value={draft.startsOn} />
              <Summary
                label={t('people.field.employment')}
                value={employmentLabel(t, draft.employmentType)}
              />
              <Summary
                label={t('people.field.teams')}
                value={teams
                  .filter((team) => draft.teamIds.includes(team.id))
                  .map((team) => team.name)
                  .join(', ')}
              />
              <Summary
                label={t('people.field.manager')}
                value={managers.find((m) => m.id === draft.managerMemberId)?.name ?? ''}
              />
              <Summary
                label={t('people.field.defaultProjectRole')}
                value={projectRoles.find((r) => r.id === draft.defaultProjectRoleId)?.name ?? ''}
              />
              <Summary label={t('people.field.location')} value={draft.location} />
              <Summary label={t('people.field.timezone')} value={draft.timezone} />
              <Summary
                label={t('people.field.tracks')}
                value={draft.tracksTime ? t('people.tracks.yes') : t('people.tracks.no')}
              />
              <Summary
                label={t('people.field.resource')}
                value={draft.resourcePlanned ? t('people.plan.yes') : t('people.plan.no')}
              />
              <Summary
                label={t('onboarding.step.sched')}
                value={scheduleSummary(t, draft, scheduleTemplates, canSettings)}
              />
              <Summary
                label={t('onboarding.step.rate')}
                value={rateSummary(t, draft, rateTemplates, currencies, canSettings)}
              />
              <Summary
                label={t('onboarding.step.leave')}
                value={
                  draft.leaveManaged
                    ? quotaSummary(t, draft, leaveTypes)
                    : t('onboarding.leave.notManaged')
                }
              />
              <Summary
                label={t('people.column.roles')}
                value={roles
                  .filter((role) => draft.roleIds.includes(role.id))
                  .map((role) => role.name)
                  .join(', ')}
              />
            </dl>
          )}

          {serverError !== null && (
            <p role="alert" className="text-destructive text-[12px]">
              {serverError}
            </p>
          )}

          <div className="mt-1 flex items-center gap-2">
            <Button
              variant="ghost"
              disabled={index === 0}
              onClick={() => {
                const previous = OB_STEPS[index - 1]?.key;
                if (previous !== undefined) go(previous);
              }}
            >
              {t('onboarding.back')}
            </Button>
            <div className="flex-1" />
            {isLast ? (
              <Button
                onClick={() => {
                  onFinish(draft);
                }}
              >
                {t('onboarding.finish')}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  const next = OB_STEPS[index + 1]?.key;
                  if (next !== undefined) go(next);
                }}
              >
                {t('onboarding.next')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

/** A card's rate today, as `11 000 Ft / 15 500 Ft` — the mockup's own `cost / target` hint. */
function rateHint(
  t: Translate,
  template: RateCardTemplate,
  currencies: readonly WorkspaceCurrency[],
): string {
  const period = periodOn(template.periods, toWireDate(new Date()));
  if (period === null) return t('onboarding.rate.noPeriod');
  const money = moneyOf(currencies, period.currencyCode);
  return `${formatMoney(Number(period.cost), money)} / ${formatMoney(Number(period.target), money)}`;
}

/**
 * The line under the picker. ⚠️ **Three states, not two** — nothing picked, picked but unpriced,
 * and priced. The middle one is a card somebody set up for next year, and saying *"no period yet"*
 * is what stops it being read as a bug later.
 */
function templateNote(
  t: Translate,
  templates: readonly RateCardTemplate[],
  currencies: readonly WorkspaceCurrency[],
  templateId: string,
): string {
  const template = templates.find((one) => one.id === templateId);
  if (template === undefined) return t('onboarding.rate.pickNote');
  const period = periodOn(template.periods, toWireDate(new Date()));
  if (period === null) return t('onboarding.rate.noPeriod');
  const money = moneyOf(currencies, period.currencyCode);
  return t('onboarding.rate.periodNote', {
    unit: period.unit === 'day' ? t('settings.rates.unit.day') : t('settings.rates.unit.hour'),
    cost: formatMoney(Number(period.cost), money),
    target: formatMoney(Number(period.target), money),
  });
}

/**
 * The pair `formatMoney` writes with. ⚠️ **The code doubles as the symbol when the currency is not
 * one of ours** — an assignment can name one the workspace has since deactivated, and `12 500 `
 * with nothing after it reads as a bug rather than as a missing symbol.
 */
function moneyOf(
  currencies: readonly WorkspaceCurrency[],
  code: string,
): { code: string; symbol: string } {
  const found = currencies.find((one) => one.code === code);
  return { code, symbol: found?.symbol ?? code };
}

/** The `done` step's one line for the rate — the mockup's `Díjkártya` summary row. */
function rateSummary(
  t: Translate,
  draft: OnboardingDraft,
  templates: readonly RateCardTemplate[],
  currencies: readonly WorkspaceCurrency[],
  canSettings: boolean,
): string {
  if (!canSettings) return t('onboarding.rate.locked');
  /* ⚠️ `onboardingRate` decides, so the summary cannot promise something the body will not send —
     an unpicked template reads as "skipped" here because that is what it will be. */
  const rate = onboardingRate(draft);
  if (rate === null) return t('onboarding.rate.skipped');
  if (rate.source === 'template') {
    const name = templates.find((one) => one.id === rate.templateId)?.name ?? '–';
    return `${name} · ${rate.fromDate}`;
  }
  const money = moneyOf(currencies, rate.currencyCode);
  const unit = rate.unit === 'day' ? t('settings.rates.unit.day') : t('settings.rates.unit.hour');
  return `${t('onboarding.rate.fromCustom')} · ${unit} · ${formatMoney(rate.cost, money)} / ${formatMoney(rate.target, money)}`;
}

/**
 * ⚠️⚠️ **LOCKED, NOT HIDDEN, AND THE SAME ARGUMENT THE MONEY REPORTS WON (C60).** A step that
 * simply vanishes teaches somebody the product cannot do the thing; a step that says *"you need
 * permission for this"* teaches them to ask their admin. The step count stays stable with it, so
 * *"step 6 of 10"* means the same thing to two colleagues comparing notes.
 */
function LockedPane({ title, note }: { readonly title: string; readonly note: string }) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border p-3">
      <Icon name="shield-check" size={15} className="text-muted-foreground mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[12.5px] font-medium">{title}</p>
        <p className="text-muted-foreground text-[11.5px] leading-relaxed">{note}</p>
      </div>
    </div>
  );
}

/**
 * ⚠️ **Monday first, out of the catalogue's own seven-letter row** rather than seven new keys: the
 * calendar, the schedules screen and this grid all label the same week, and three copies of
 * *H,K,Sze…* is three places for one of them to start on Sunday.
 */
const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

function weekdayLabel(t: Translate, index: number): string {
  return t('calendar.weekdays').split(',')[index] ?? '';
}

/** The picked schedule in a line — *Fix · 40:00 / hét*. */
function scheduleNote(
  t: Translate,
  templates: readonly WorkScheduleTemplate[],
  templateId: string,
): string {
  const template = templates.find((one) => one.id === templateId);
  if (template === undefined) return t('onboarding.sched.pickNote');
  return t('onboarding.sched.templateNote', {
    type: template.type === 'flex' ? t('onboarding.sched.flex') : t('onboarding.sched.fix'),
    hours: formatHours(template.weeklyHours),
  });
}

/**
 * ⚠️ **A flex week has no daily breakdown, and the line says so** rather than printing a total of
 * seven zeroes — which is what the days array holds for a flex schedule.
 */
function scheduleTotal(t: Translate, draft: OnboardingDraft): string {
  if (draft.schedType === 'flex') {
    return t('onboarding.sched.flexTotal', {
      hours: formatHours(String(numberOf(draft.schedWeekly) || 0)),
    });
  }
  const total = draft.schedDays.reduce((sum, one) => sum + (numberOf(one) || 0), 0);
  return t('onboarding.sched.fixTotal', { hours: formatHours(String(total)) });
}

/** The `done` step's one line for the schedule. */
function scheduleSummary(
  t: Translate,
  draft: OnboardingDraft,
  templates: readonly WorkScheduleTemplate[],
  canSettings: boolean,
): string {
  if (!canSettings) return t('onboarding.sched.locked');
  if (!draft.tracksTime) return t('onboarding.sched.notNeededShort');
  const schedule = onboardingSchedule(draft);
  if (schedule === null) return t('onboarding.sched.skipped');
  if (schedule.source === 'template') {
    const name = templates.find((one) => one.id === schedule.templateId)?.name ?? '–';
    return `${name} · ${schedule.fromDate}`;
  }
  return `${t('onboarding.sched.fromCustom')} · ${
    schedule.type === 'flex' ? t('onboarding.sched.flex') : t('onboarding.sched.fix')
  } · ${formatHours(String(schedule.weeklyHours))}`;
}

/** The `done` step's one line for the quotas — *25 nap éves szabadság · 15 nap betegszabadság*. */
function quotaSummary(t: Translate, draft: OnboardingDraft, types: readonly LeaveType[]): string {
  const rows = onboardingQuotas(draft);
  if (rows.length === 0) return t('onboarding.leave.noQuotas');
  return rows
    .map((one) => {
      const name = types.find((type) => type.id === one.leaveTypeId)?.name ?? '';
      return `${t('onboarding.leave.dayCount', { count: one.days })} ${name.toLowerCase()}`.trim();
    })
    .join(' · ');
}

/** ⚠️ An em-dash for an empty value, never a blank row — the kit's rule. */
function Summary({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex items-baseline gap-2 border-b py-1 last:border-0">
      <dt className="text-muted-foreground w-[150px] shrink-0">{label}</dt>
      <dd className={value === '' ? 'text-muted-foreground' : 'font-medium'}>
        {value === '' ? '–' : value}
      </dd>
    </div>
  );
}
